import type { ToolConfig } from "../types";
import { createSIPTool } from "../factories";

export const educationTools: ToolConfig[] = [
  // 2. Education Cost Planner
  {
    slug: "education-cost-planner",
    category: "education",
    title: "Future Education Cost Planner",
    shortTitle: "Education Cost Planning",
    description: "Plan total education costs with year-wise financial projections",
    keywords: ["education cost", "future cost", "inflation", "college cost"],
    inputs: [
      { type: "number", key: "currentCost", label: "Current Annual Cost", prefix: "₹", min: 50000, max: 5000000, step: 50000, default: 500000 },
      { type: "number", key: "yearsUntilEducation", label: "Years Until Education Starts", suffix: "years", min: 1, max: 20, step: 1, default: 5 },
      { type: "number", key: "durationYears", label: "Course Duration", suffix: "years", min: 1, max: 10, step: 1, default: 4 },
      { type: "number", key: "inflationRate", label: "Education Inflation Rate", suffix: "%", min: 5, max: 15, step: 0.5, default: 8 },
    ],
    calculate(v) {
      const cost = v.currentCost as number;
      const years = v.yearsUntilEducation as number;
      const duration = v.durationYears as number;
      const inflation = (v.inflationRate as number) / 100;

      let totalCost = 0;
      for (let i = 0; i < duration; i++) {
        const yearIndex = years + i;
        const yearCost = cost * Math.pow(1 + inflation, yearIndex);
        totalCost += yearCost;
      }
      totalCost = Math.round(totalCost);

      const firstYearCost = Math.round(cost * Math.pow(1 + inflation, years));

      return { totalCost, firstYearCost, inflation: Math.round(inflation * 100) };
    },
    resultFields: [
      { key: "totalCost", label: "Total Course Cost", format: "currency", isPrimary: true },
      { key: "firstYearCost", label: "First Year Cost", format: "currency" },
    ],
    content: {
      whatIs: "Education cost planning requires comprehensive projection of all expenses from school entry through graduation, accounting for inflation, course-specific fees, and institution-type variations. Total education costs for Indian schools: government schools K-12 (₹5-15 lakhs for 13 years), private schools (₹20-60 lakhs), international schools (₹60-150 lakhs). College costs: government colleges (₹5-10 lakhs for 4-year), private engineering colleges (₹30-60 lakhs), overseas universities (₹40-200 lakhs). Education inflation typically runs 10-12% annually, requiring substantial upward adjustment in 10+ year projections. Effective planning includes estimating year-wise costs, identifying funding sources, and structuring savings plans aligned with education timeline.",
      faqs: [
        { question: "What's the average education inflation in India?", answer: "8-10% annually (higher than general inflation: 5-6%). Top schools inflate at 10-12%. Plan with 8-10% assumption for accuracy." },
        { question: "How much should I save monthly for child's education in India?", answer: "Use SIP calculator. For ₹30L goal in 15 years at 12% returns: ₹8,000-10,000/month. Adjust amount based on child's age and school choice." },
        { question: "What education costs are covered by insurance in India?", answer: "Education insurance (endowment plans) cover tuition, living expenses, books. Typical cover: ₹10-50L. Combination of education fund + insurance recommended." },
        { question: "Is it better to save in PPF or education funds in India?", answer: "PPF: guaranteed 7% returns, tax-free maturity, government-backed safety. Funds: 10-12% potential. For 15-year goals: funds better; shorter: PPF safer." },
        { question: "Should I take education loan or use savings in India?", answer: "If savings earn 12%+ and loan costs 9%, invest. If uncertain, use 50-50: partial savings + partial loan minimizes interest burden." },
      ],
      relatedSlugs: ["education/education-loan-emi", "education/child-education-fund", "personal-finance/savings-goal-calculator"],
    },
  },

  // 3. Study Abroad Cost
  {
    slug: "study-abroad-cost",
    category: "education",
    title: "Study Abroad Total Cost Calculator",
    shortTitle: "Study Abroad Cost",
    description: "Calculate total costs for studying abroad in USA, UK, Australia",
    keywords: ["study abroad", "overseas education", "tuition cost", "living cost"],
    inputs: [
      { type: "number", key: "tuitionPerYear", label: "Annual Tuition Fee", prefix: "$", min: 5000, max: 100000, step: 5000, default: 40000 },
      { type: "number", key: "livingPerMonth", label: "Monthly Living Cost", prefix: "$", min: 1000, max: 10000, step: 500, default: 2000 },
      { type: "number", key: "years", label: "Course Duration", suffix: "years", min: 1, max: 5, step: 1, default: 2 },
      { type: "number", key: "inrRate", label: "USD to INR Exchange Rate", suffix: "INR/USD", min: 70, max: 100, step: 0.5, default: 83 },
    ],
    calculate(v) {
      const tuition = v.tuitionPerYear as number;
      const living = v.livingPerMonth as number;
      const years = v.years as number;
      const rate = v.inrRate as number;

      const totalTuition = tuition * years;
      const totalLiving = living * 12 * years;
      const miscellaneous = (totalTuition + totalLiving) * 0.10; // 10% for travel, books, etc.

      const totalUSD = totalTuition + totalLiving + miscellaneous;
      const totalINR = Math.round(totalUSD * rate);

      return { totalUSD: Math.round(totalUSD), totalINR, tuitionUSD: Math.round(totalTuition), livingUSD: Math.round(totalLiving) };
    },
    resultFields: [
      { key: "totalINR", label: "Total Cost in INR", format: "currency", isPrimary: true },
      { key: "totalUSD", label: "Total Cost in USD", format: "currency" },
      { key: "tuitionUSD", label: "Total Tuition (USD)", format: "currency" },
      { key: "livingUSD", label: "Total Living (USD)", format: "currency" },
    ],
    content: {
      whatIs: "Study abroad costs: US/UK: $40-60k/year tuition + $2-3k/month living = $55-75k/year total. Australia/Canada: $30-45k/year + $1.5-2k living. India students typically spend $30-60k/year (₹25-50L). Total 2-year MBA abroad: ₹40-100L. Scholarships can reduce 50-100%. Student visa allows part-time work (10-20 hrs/week) to offset costs. Loans available up to ₹40L from Indian banks.",
      faqs: [
        { question: "How much does an MS degree cost in US for Indian student?", answer: "Tuition: $25-50k/year; living: $20-30k/year; total: $45-80k/year (₹37-66L). 2-year program: ₹74-132L. Scholarships can reduce 50-100%." },
        { question: "What are the cheapest countries for Indian students to study?", answer: "Philippines, Thailand, Malaysia: $10-20k/year total. Eastern Europe: $15-25k/year. UK/US expensive: $50-80k/year. Australia: $35-45k/year." },
        { question: "Can Indian students work while studying abroad?", answer: "US: 10-20 hrs/week on campus (optional practical training: 12-24 months post-graduation). UK: 20 hrs/week during term. Work helps offset ₹3-5L annual living costs." },
        { question: "What education loans are available for study abroad in India?", answer: "Most banks offer ₹20-40L education loans for abroad. Government schemes: PMEGP (subsidized). Interest: 8-10% p.a. Repayment grace: 1-2 years post-course." },
        { question: "How much scholarship can Indian student get for abroad study?", answer: "Merit-based: 0-100% tuition coverage depending on scores. Government: ICCR, AICTE scholarships (partial). Most students get 0-25% scholarship; use loans for remainder." },
      ],
      relatedSlugs: ["education/education-loan-emi", "education/education-cost-planner", "education/pgdm-vs-mba-cost"],
    },
  },

  // 4. College Fee Calculator
  {
    slug: "college-fee-calculator",
    category: "education",
    title: "4-Year College Total Cost Calculator",
    shortTitle: "College Cost (4-Year)",
    description: "Calculate college tuition and fees across Indian institutions",
    keywords: ["college cost", "4 year degree", "total cost", "college fees"],
    inputs: [
      { type: "number", key: "annualFee", label: "Annual College Fee", prefix: "₹", min: 100000, max: 2000000, step: 50000, default: 500000 },
      { type: "number", key: "booksCost", label: "Annual Books & Materials", prefix: "₹", min: 10000, max: 200000, step: 10000, default: 50000 },
      { type: "number", key: "accommodationCost", label: "Annual Accommodation (if hostel)", prefix: "₹", min: 0, max: 500000, step: 25000, default: 150000 },
      { type: "number", key: "transportCost", label: "Annual Transport", prefix: "₹", min: 0, max: 200000, step: 10000, default: 30000 },
    ],
    calculate(v) {
      const fee = v.annualFee as number;
      const books = v.booksCost as number;
      const accommodation = v.accommodationCost as number;
      const transport = v.transportCost as number;

      const annualTotal = fee + books + accommodation + transport;
      const fourYearTotal = annualTotal * 4;
      const perMonth = Math.round(annualTotal / 12);

      return { annualTotal, fourYearTotal, perMonth };
    },
    resultFields: [
      { key: "fourYearTotal", label: "Total 4-Year Cost", format: "currency", isPrimary: true },
      { key: "annualTotal", label: "Annual Cost", format: "currency" },
      { key: "perMonth", label: "Monthly Cost", format: "currency" },
    ],
    content: {
      whatIs: "4-year college total cost includes tuition (₹2-10L/year), books (₹50k-2L/year), accommodation (₹1-5L/year if hostel), and transport. Total 4-year cost: ₹8-60L depending on college tier. Government colleges: ₹3-8L total. Private colleges: ₹15-40L. Premium colleges: ₹30-80L. Planning early via education fund SIPs or loans ensures financial readiness. Many students combine partial loans with scholarships.",
      faqs: [
        { question: "What's the average 4-year college cost in India?", answer: "Government college: ₹3-8L total. Private college: ₹15-40L. Tier-1 colleges: ₹30-80L. Varies by city, course, and college. Add living costs separately." },
        { question: "Are scholarships available for college in India?", answer: "Merit-based: 25-100% depending on scores. Need-based: limited availability. Government schemes: SC/ST reserved. Apply through college/state education department." },
        { question: "Can students work during college in India?", answer: "Yes, part-time work (10-15 hrs/week, max ₹200-300/hr) helps. Many internships offer ₹20-40k/month during summer. Reduces financial burden on parents." },
        { question: "What are add-on costs during college not included above?", answer: "Exam fees, lab charges, project expenses, co-curricular activities, field trips, health insurance. Add 10-15% buffer to estimated cost." },
        { question: "Should I take college loan or pay from savings in India?", answer: "If education ROI (salary post-graduation) is 12%+ and loan costs 9%, consider loan. Otherwise, pay from savings/SIP if accumulated. Avoid debt for low-ROI courses." },
      ],
      relatedSlugs: ["education/education-cost-planner", "education/education-loan-emi", "personal-finance/savings-goal-calculator"],
    },
  },

  // 5. Child Education Fund (SIP)
  createSIPTool({
    slug: "child-education-fund",
    category: "education",
    title: "Child Education Fund SIP Calculator",
    shortTitle: "Education Fund SIP",
    description: "Plan education fund for child starting school through graduation",
    keywords: ["education SIP", "education fund", "monthly investment", "education savings"],
    defaultMonthly: 5000,
    maxMonthly: 100000,
    defaultRate: 12,
    defaultYears: 15,
    whatIs: "Education fund SIP (Systematic Investment Plan) helps accumulate corpus for child's education starting early. Expected returns: 10-12% p.a. through balanced/equity funds. Investing ₹5,000/month for 15 years at 12% returns: ₹13L+ corpus. Earlier start = lower monthly contribution needed. Combines power of compounding with disciplined investing. Tax-deductible under Section 80C if through eligible funds.",
    faqs: [
      { question: "When should parents start education fund SIP in India?", answer: "Ideally at birth or within first 5 years. Starting at age 5 for 18-year-old: ₹8k/month. At age 10: ₹15k/month. Starting early cuts monthly burden significantly." },
      { question: "What return should I expect from education fund SIP?", answer: "Equity fund: 10-12% p.a. historically. Balanced fund: 8-10%. Debt fund: 6-7%. For 15-year horizon: equity/balanced recommended for better growth." },
      { question: "Are education fund SIPs tax-deductible in India?", answer: "Not specifically, but ELSS funds (equity-linked savings scheme) provide Section 80C deduction (max ₹1.5L/year). Returns tax-free if held 3+ years." },
      { question: "Should I use education-specific fund or regular mutual fund?", answer: "Regular balanced/equity funds often better. Education funds charge higher fees (0.5-1% extra). Invest in low-cost index funds or regular balanced funds." },
      { question: "How much monthly SIP is needed for ₹30L education goal in India?", answer: "15-year goal at 12% returns: ₹10,000/month. 10-year goal: ₹17,000/month. Adjust based on target amount and expected returns." },
    ],
    relatedSlugs: ["education/education-cost-planner", "education/education-loan-emi", "personal-finance/savings-goal-calculator"],
  }),

  // 6. Tuition Fee Increase
  {
    slug: "tuition-fee-increase",
    category: "education",
    title: "Future Tuition Fee Calculator",
    shortTitle: "Tuition Fee Inflation",
    description: "Project education costs with inflation rates (10-12% annually)",
    keywords: ["tuition increase", "fee increase", "education inflation", "future fees"],
    inputs: [
      { type: "number", key: "currentFee", label: "Current Annual Tuition Fee", prefix: "₹", min: 50000, max: 5000000, step: 50000, default: 500000 },
      { type: "number", key: "annualIncreasePercent", label: "Annual Fee Increase", suffix: "%", min: 2, max: 20, step: 1, default: 8 },
      { type: "number", key: "years", label: "Time Period", suffix: "years", min: 1, max: 20, step: 1, default: 5 },
    ],
    calculate(v) {
      const fee = v.currentFee as number;
      const increase = (v.annualIncreasePercent as number) / 100;
      const years = v.years as number;

      const futureFee = Math.round(fee * Math.pow(1 + increase, years));
      const totalIncrease = futureFee - fee;
      const increasePercent = Math.round((totalIncrease / fee) * 100);

      return { currentFee: fee, futureFee, totalIncrease, increasePercent, years };
    },
    resultFields: [
      { key: "futureFee", label: "Future Annual Fee", format: "currency", isPrimary: true },
      { key: "currentFee", label: "Current Annual Fee", format: "currency" },
      { key: "totalIncrease", label: "Total Increase", format: "currency" },
      { key: "increasePercent", label: "Increase %", format: "percent" },
    ],
    content: {
      whatIs: "Tuition fees in India increase 8-12% annually (higher than general inflation). A ₹5L/year school fee becomes ₹8.6L in 10 years at 10% growth. Planning for this inflation is critical: ₹5L today ≠ ₹5L in 10 years. Education fund must account for growth. IIT/NIT fees: minimal increase (5-7% annually). Private school/college: 8-15% annually. International schools: 10-12% annually.",
      faqs: [
        { question: "Why do education fees increase faster than general inflation in India?", answer: "Infrastructure investment, faculty salary increases, technology upgrades, international accreditation costs. Premium institutions increase 10-15%; government schools 3-5%." },
        { question: "What's the average annual fee increase for Indian schools?", answer: "Government schools: 3-5%. Budget private schools: 7-10%. Premium schools: 10-15%. Tier-1 colleges: 10-12%. Plan with 8-10% assumption." },
        { question: "Can parents negotiate school fee increases in India?", answer: "Limited negotiation. Some schools allow hardship waivers. Fee commissions/authorities sometimes cap increases. Transparency in fee structure varies by state." },
        { question: "How do school fee increases affect education loan EMI in India?", answer: "Loan EMI doesn't change once sanctioned. But if fees increase and loan insufficient, top-up loan needed. Get loan amount 20-30% higher than current estimate." },
        { question: "Are fee increases valid for multi-year contracts in India?", answer: "Varies by state. Some contracts have fixed fees; others allow 5-8% annual increase. Read contract carefully. Government schools regulated; private schools less regulated." },
      ],
      relatedSlugs: ["education/education-cost-planner", "personal-finance/inflation-calculator", "education/child-education-fund"],
    },
  },

  // 7. Coaching Cost Calculator
  {
    slug: "coaching-cost-calculator",
    category: "education",
    title: "Coaching Institute Total Cost Calculator",
    shortTitle: "Coaching Cost",
    description: "Calculate JEE, NEET, UPSC coaching and exam expenses",
    keywords: ["coaching cost", "JEE coaching", "NEET coaching", "competitive exam"],
    inputs: [
      { type: "number", key: "monthlyFee", label: "Monthly Coaching Fee", prefix: "₹", min: 2000, max: 50000, step: 1000, default: 10000 },
      { type: "number", key: "durationMonths", label: "Coaching Duration", suffix: "months", min: 3, max: 24, step: 1, default: 12 },
      { type: "number", key: "additionalBooks", label: "Books & Materials Cost", prefix: "₹", min: 0, max: 100000, step: 10000, default: 30000 },
      { type: "number", key: "testSeriesCost", label: "Test Series & Mock Cost", prefix: "₹", min: 0, max: 50000, step: 5000, default: 15000 },
    ],
    calculate(v) {
      const monthly = v.monthlyFee as number;
      const duration = v.durationMonths as number;
      const books = v.additionalBooks as number;
      const tests = v.testSeriesCost as number;

      const totalFee = monthly * duration;
      const totalCost = totalFee + books + tests;

      return { totalFee, totalCost, monthlyAverage: Math.round(totalCost / duration) };
    },
    resultFields: [
      { key: "totalCost", label: "Total Coaching Cost", format: "currency", isPrimary: true },
      { key: "totalFee", label: "Coaching Fees", format: "currency" },
      { key: "monthlyAverage", label: "Average Monthly Cost", format: "currency" },
    ],
    content: {
      whatIs: "Competitive exam coaching (JEE, NEET, UPSC) costs: offline classes ₹8-20k/month, online ₹2-8k/month. 1-2 year coaching: ₹1.5-5L total. Books/materials: ₹30-50k. Test series: ₹10-20k. Total: ₹2-6L. Most Indian students take coaching; success rate higher with quality coaching. Online coaching increasingly popular (lower cost, flexibility). UPSC coaching: ₹3-10L for 2+ years due to lengthy preparation.",
      faqs: [
        { question: "How much does JEE/NEET coaching cost in India?", answer: "Offline: ₹10-20k/month (₹1.2-2.4L/year). Online: ₹2-8k/month (₹24-96k/year). 2-year JEE coaching: ₹3-5L total with books/tests included." },
        { question: "Is coaching necessary for JEE/NEET success in India?", answer: "Not necessary but helpful. 80%+ aspirants take coaching. Success rates: 5-10% without coaching, 20-30% with quality coaching. Self-study + selective coaching hybrid approach works well." },
        { question: "What's the ROI of competitive exam coaching in India?", answer: "IIT engineering graduate earns ₹12-30L package. NEET doctor ₹8-50L depending on specialization. Coaching cost (₹3-5L) justified by salary increase." },
        { question: "Are online coaching platforms cheaper than offline in India?", answer: "Yes, 50-70% cheaper. Online: ₹3-8k/month. Offline: ₹10-20k/month. Quality similar; offline offers peer support, discipline, doubt sessions." },
        { question: "Can I get coaching fee refund if I don't succeed in India?", answer: "Refunds rare. Most platforms offer guarantee schemes: free repeat if not qualified (limited). Always read terms. Success depends on student effort, not just coaching." },
      ],
      relatedSlugs: ["education/education-cost-planner", "education/education-loan-emi", "personal-finance/savings-goal-calculator"],
    },
  },

  // 8. Education ROI Calculator
  {
    slug: "education-roi-calculator",
    category: "education",
    title: "Education ROI Calculator",
    shortTitle: "Education ROI",
    description: "Calculate return on education investment in career earnings",
    keywords: ["education ROI", "education return", "salary increase", "education value"],
    inputs: [
      { type: "number", key: "educationCost", label: "Total Education Cost", prefix: "₹", min: 500000, max: 100000000, step: 500000, default: 20000000 },
      { type: "number", key: "currentSalary", label: "Current Salary (Before Education)", prefix: "₹", min: 200000, max: 100000000, step: 100000, default: 500000 },
      { type: "number", key: "salaryAfter", label: "Projected Salary (After Education)", prefix: "₹", min: 500000, max: 100000000, step: 100000, default: 1500000 },
      { type: "number", key: "yearsToRecoup", label: "Years to Recoup Investment", suffix: "years", min: 1, max: 40, step: 1, default: 10 },
    ],
    calculate(v) {
      const cost = v.educationCost as number;
      const before = v.currentSalary as number;
      const after = v.salaryAfter as number;
      const years = v.yearsToRecoup as number;

      const annualGain = after - before;
      const totalGainInPeriod = annualGain * years;
      const netROI = totalGainInPeriod - cost;
      const roiPercent = Math.round((netROI / cost) * 100);
      const breakEvenYears = Math.round(cost / Math.max(1, annualGain));

      return { annualGain, totalGainInPeriod, netROI, roiPercent, breakEvenYears };
    },
    resultFields: [
      { key: "netROI", label: "Net ROI After 'Recoup Years'", format: "currency", isPrimary: true },
      { key: "roiPercent", label: "ROI %", format: "percent" },
      { key: "annualGain", label: "Annual Salary Gain", format: "currency" },
      { key: "breakEvenYears", label: "Break-Even Period", format: "years" },
    ],
    content: {
      whatIs: "Education ROI shows whether education investment is worthwhile financially. IIT engineering: ₹20L cost, ₹30L salary vs ₹5L without education = ₹25L annual gain, ROI positive in 1 year. Low-demand degree: ₹15L cost, ₹8L salary gain = negative ROI in 5 years. Factors: degree quality, field demand, placement rates, and market conditions. Always consider non-monetary benefits (skills, confidence, network).",
      faqs: [
        { question: "What's a good ROI for education in India?", answer: "Breakeven in 2-4 years = good. Breakeven in 1-2 years = excellent. >5 years = reconsider course choice. Plus non-financial benefits." },
        { question: "Which courses have best ROI in India?", answer: "IIT/NIT engineering: ₹30-50L salary. AIIMS medicine: ₹20-50L (specialty dependent). Top MBA: ₹20-40L. CS degree: ₹15-25L. Arts degree: ₹5-10L." },
        { question: "Is education always a good investment in India?", answer: "Usually yes for quality institutions (IIT/top colleges). Yes for in-demand fields (CS, engineering, medicine). No for low-demand courses from poor colleges." },
        { question: "How does placement rate affect education ROI in India?", answer: "High placement (95%+): strong ROI. Low placement (50-70%): risky. Check college placement record, average salary, top-100 placements before enroll." },
        { question: "Should I take education loan if ROI is positive in 2 years in India?", answer: "Yes, if loan cost (9% p.a.) < salary gain (30%+). Loan breakeven: 3-4 years. After breakeven: debt-free returns accelerate. Consider loan viability." },
      ],
      relatedSlugs: ["education/education-cost-planner", "education/education-loan-emi", "education/study-loan-comparison"],
    },
  },

  // 9. Scholarship Savings
  {
    slug: "scholarship-savings",
    category: "education",
    title: "Scholarship Savings Calculator",
    shortTitle: "Scholarship Savings",
    description: "Estimate education cost savings through available scholarships",
    keywords: ["scholarship", "scholarship savings", "financial aid", "education subsidy"],
    inputs: [
      { type: "number", key: "totalCost", label: "Total Education Cost", prefix: "₹", min: 500000, max: 100000000, step: 500000, default: 20000000 },
      { type: "number", key: "scholarshipPercent", label: "Scholarship Award %", suffix: "%", min: 10, max: 100, step: 5, default: 50 },
      { type: "toggle", key: "needBased", label: "Need-Based Scholarship (Extends if maintain grade)", default: false },
    ],
    calculate(v) {
      const cost = v.totalCost as number;
      const scholarship = (v.scholarshipPercent as number) / 100;
      const needBased = v.needBased as boolean;

      const scholarshipAmount = Math.round(cost * scholarship);
      const amountToPay = cost - scholarshipAmount;
      const loanNeeded = Math.max(0, amountToPay - (amountToPay * 0.20)); // Assume 20% savings buffer

      return { scholarshipAmount, amountToPay, loanNeeded, savings: scholarshipAmount };
    },
    resultFields: [
      { key: "scholarshipAmount", label: "Scholarship Award Amount", format: "currency", isPrimary: true },
      { key: "amountToPay", label: "Amount Still to Pay", format: "currency" },
      { key: "loanNeeded", label: "Potential Loan Needed", format: "currency" },
    ],
    content: {
      whatIs: "Scholarships reduce education cost burden significantly. Merit-based: 25-100% depending on scores/talent. Need-based: varies by institution. Government scholarships: SC/ST reserved seats (75-100% fee waiver). Most Indian students combine 0-25% scholarship with education loans for remainder. Scholarship+ part-time work can make education nearly free. Maintain required GPA to retain scholarship (typically 6.5-7.5/10).",
      faqs: [
        { question: "How much scholarship can Indian student get for college?", answer: "Merit: 25-100% tuition (top scorers get full). Need-based: 25-75% (limited by institution budget). Government: SC/ST/OBC get fee waiver. Average: 0-25% for general category." },
        { question: "Are scholarships available for private colleges in India?", answer: "Yes, merit-based (25-100% depending on entrance scores). Need-based limited. Some colleges: 10-20% on merit. Top students: 50-100% scholarship." },
        { question: "What happens to scholarship if grades drop in India?", answer: "Most scholarships require 6.5-7.5/10 GPA. Failing to maintain: scholarship cancelled. Some scholarship: no GPA condition (full duration guaranteed)." },
        { question: "Can I combine scholarship with education loan in India?", answer: "Yes, very common. Get scholarship (reduces cost), then take loan for remainder. Loan burden reduced significantly by scholarship amount." },
        { question: "How to find and apply for scholarships in India?", answer: "Central portal: scholarships.gov.in aggregates government/NGO scholarships. College website: merit/need-based scholarships. NGOs: various caste/community-based. Apply early (deadlines: June-July)." },
      ],
      relatedSlugs: ["education/education-loan-emi", "education/education-cost-planner", "personal-finance/savings-goal-calculator"],
    },
  },

  // 10. Hostel Cost Calculator
  {
    slug: "hostel-cost-calculator",
    category: "education",
    title: "Hostel & Mess Cost Calculator",
    shortTitle: "Hostel Cost",
    description: "Calculate hostel, mess, and living expenses for students",
    keywords: ["hostel cost", "mess charges", "accommodation cost", "living expenses"],
    inputs: [
      { type: "number", key: "monthlyRent", label: "Monthly Room Rent", prefix: "₹", min: 2000, max: 50000, step: 1000, default: 15000 },
      { type: "number", key: "messCharges", label: "Monthly Mess Charges", prefix: "₹", min: 2000, max: 15000, step: 500, default: 5000 },
      { type: "number", key: "utilitiesPerMonth", label: "Monthly Utilities (Electric, Water, WiFi)", prefix: "₹", min: 500, max: 5000, step: 500, default: 1500 },
      { type: "number", key: "otherMonthly", label: "Other Monthly Expenses", prefix: "₹", min: 1000, max: 10000, step: 1000, default: 3000 },
    ],
    calculate(v) {
      const rent = v.monthlyRent as number;
      const mess = v.messCharges as number;
      const utilities = v.utilitiesPerMonth as number;
      const other = v.otherMonthly as number;

      const monthlyTotal = rent + mess + utilities + other;
      const annualTotal = monthlyTotal * 12;
      const fourYearTotal = annualTotal * 4;

      return { monthlyTotal, annualTotal, fourYearTotal };
    },
    resultFields: [
      { key: "annualTotal", label: "Annual Cost", format: "currency", isPrimary: true },
      { key: "monthlyTotal", label: "Monthly Cost", format: "currency" },
      { key: "fourYearTotal", label: "4-Year Total", format: "currency" },
    ],
    content: {
      whatIs: "Hostel cost: rent ₹10-30k/month, mess ₹4-8k/month, utilities ₹1-2k, miscellaneous ₹2-4k. Total: ₹17-44k/month (₹2-5.3L annually). 4-year hostel cost: ₹8-21L. Day scholar: ₹5-15k/month transport + food (₹1-2L annually). Girls' hostel: typically 20-30% higher. Private hostels: cheaper (₹10-15k all-inclusive) but less amenities.",
      faqs: [
        { question: "What's the average hostel cost for college students in India?", answer: "Government college hostel: ₹3-8k/month. Private hostel: ₹8-15k. Urban cities: ₹15-25k/month. Includes room, mess, utilities. Day scholar: ₹5-15k/month." },
        { question: "Are hostel costs cheaper than day scholar in India?", answer: "Often yes. Hostel: ₹20k/month all-inclusive. Day scholar: ₹25-35k (rent+transport+food separate). Depends on city and location." },
        { question: "Can students negotiate hostel fees with colleges in India?", answer: "Rarely. Fixed fees set by college. Minor negotiation for group rates or long-term booking. Fees increase 5-10% annually." },
        { question: "Are hostel expenses covered by education loans in India?", answer: "Yes, total education cost (tuition + living) covered by loan. Hostel fees included in monthly disbursement. Use funds for hostel/living expenses." },
        { question: "What costs are covered in hostel rent in India?", answer: "Typically: room, mess, utilities (electric, water), WiFi, basic maintenance. Laundry, mobile recharge, personal items: separate/from pocket money." },
      ],
      relatedSlugs: ["education/education-cost-planner", "education/college-fee-calculator", "personal-finance/cost-of-living-calculator"],
    },
  },

  // 11. Education Tax Benefit
  {
    slug: "education-tax-benefit",
    category: "education",
    title: "Section 80E Education Loan Interest Deduction",
    shortTitle: "Tax Benefit (Section 80E)",
    description: "Calculate Section 80E deduction and education tax benefits",
    keywords: ["80E deduction", "education tax benefit", "loan interest deduction", "tax savings"],
    inputs: [
      { type: "number", key: "loanInterest", label: "Annual Loan Interest Paid", prefix: "₹", min: 50000, max: 5000000, step: 50000, default: 500000 },
      { type: "select", key: "taxSlab", label: "Income Tax Slab", options: [{ value: "20", label: "20% (₹10-20L)" }, { value: "30", label: "30% (₹20-50L)" }, { value: "slab", label: "Progressive (varies 5-30%)" }], default: "30" },
    ],
    calculate(v) {
      const interest = v.loanInterest as number;
      const slab = v.taxSlab as string;

      let rate = 0;
      if (slab === "20") rate = 0.20;
      else if (slab === "30") rate = 0.30;
      else rate = 0.25; // Average for progressive

      const taxSavings = Math.round(interest * rate);
      const effectiveInterestCost = interest - taxSavings;

      return { loanInterest: interest, taxSavings, effectiveInterestCost };
    },
    resultFields: [
      { key: "taxSavings", label: "Annual Tax Savings", format: "currency", isPrimary: true },
      { key: "effectiveInterestCost", label: "Effective Interest Cost", format: "currency" },
      { key: "loanInterest", label: "Total Interest Paid", format: "currency" },
    ],
    content: {
      whatIs: "Section 80E allows unlimited deduction of education loan interest (no cap) for 8 years from repayment start. Full interest amount deductible unlike other loans (₹2L cap on home loan interest). If paying ₹5L annual interest at 30% tax slab: ₹1.5L tax savings yearly. Over 8-year repayment: ₹12L total tax saving on ₹40L interest. Makes education loan after-tax cost much lower than nominal rate.",
      faqs: [
        { question: "What's Section 80E in education loans in India?", answer: "Section 80E: entire education loan interest deductible (no limit). Available for 8 years from repayment start. Reduces taxable income, saves 20-30% on interest cost." },
        { question: "How much tax savings can I get from education loan in India?", answer: "Depends on interest paid and tax slab. ₹5L interest at 30% slab: ₹1.5L yearly savings. Over 8 years: ₹12L total savings on ₹40L interest." },
        { question: "Is the interest deduction limited or unlimited under 80E?", answer: "Unlimited. Full interest amount deductible (no cap). Unique to education loans; other loans have ₹2L or other limits." },
        { question: "Can parents claim 80E if student takes loan in India?", answer: "Yes, parent can claim 80E if they are loan guarantor/co-borrower. If student is sole borrower (without parent), student claims 80E once working." },
        { question: "How do I claim 80E deduction in ITR in India?", answer: "File ITR, provide education loan documents (sanction letter, interest certificate from bank). Self-employed: Schedule EI; salaried: noted in ITR-1 schedule." },
      ],
      relatedSlugs: ["education/education-loan-emi", "education/education-cost-planner", "personal-finance/budget-calculator"],
    },
  },

  // 12. Student Budget Calculator
  {
    slug: "student-budget-calculator",
    category: "education",
    title: "Student Monthly Budget Planner",
    shortTitle: "Student Budget",
    description: "Create monthly education budget for various education levels",
    keywords: ["student budget", "monthly expenses", "student spending", "financial management"],
    inputs: [
      { type: "number", key: "accommodation", label: "Accommodation (Hostel/Rent)", prefix: "₹", min: 3000, max: 50000, step: 1000, default: 15000 },
      { type: "number", key: "food", label: "Food & Groceries", prefix: "₹", min: 2000, max: 20000, step: 1000, default: 5000 },
      { type: "number", key: "transport", label: "Transport & Commute", prefix: "₹", min: 500, max: 10000, step: 500, default: 2000 },
      { type: "number", key: "books", label: "Books & Stationery", prefix: "₹", min: 500, max: 5000, step: 500, default: 1500 },
      { type: "number", key: "entertainment", label: "Entertainment & Socializing", prefix: "₹", min: 1000, max: 20000, step: 1000, default: 5000 },
      { type: "number", key: "utilities", label: "Utilities & Internet", prefix: "₹", min: 500, max: 5000, step: 500, default: 1500 },
      { type: "number", key: "personal", label: "Personal Care & Misc", prefix: "₹", min: 500, max: 5000, step: 500, default: 2000 },
    ],
    calculate(v) {
      const total = (v.accommodation as number) + (v.food as number) + (v.transport as number) + (v.books as number) + (v.entertainment as number) + (v.utilities as number) + (v.personal as number);
      const annual = total * 12;

      return { monthlyTotal: total, annualTotal: annual };
    },
    resultFields: [
      { key: "monthlyTotal", label: "Monthly Budget", format: "currency", isPrimary: true },
      { key: "annualTotal", label: "Annual Budget", format: "currency" },
    ],
    content: {
      whatIs: "Student budget planning ensures financial discipline and prevents overspending. Average student expenses: ₹20-35k/month (accommodation: 50%, food: 20%, entertainment: 15%, others: 15%). Living within budget develops lifelong financial habits. Many students work part-time (₹10-15k/month) to cover expenses. Parents typically provide ₹20-30k/month; student covers remainder via work and savings.",
      faqs: [
        { question: "What's the average monthly budget for student in India?", answer: "Hostel student: ₹20-35k/month. Day scholar: ₹15-25k/month. Cities like Delhi/Mumbai: 30-40% higher. Includes all regular expenses." },
        { question: "How can students reduce monthly expenses in India?", answer: "Share accommodation (reduce ₹5k/month), cook meals at home (save ₹2-3k), use public transport (save ₹1-2k), minimize entertainment (save ₹2k). Total: ₹10-12k savings." },
        { question: "Should students take part-time work to fund education in India?", answer: "Yes, 10-20 hrs/week at ₹200-300/hr = ₹8-12k/month. Covers living expenses, develops skills, relieves parent burden. Balance with studies." },
        { question: "How much should parents give as pocket money in India?", answer: "₹15-25k/month typical. Adjust based on city, lifestyle, and family capacity. Encourage student to budget and save remainder." },
        { question: "What expenses are often overlooked in student budget in India?", answer: "Travel home (₹2-5k quarterly), medical expenses (₹2-4k annually), clothes/shoes (₹2-3k yearly), emergency funds. Add 10% buffer." },
      ],
      relatedSlugs: ["personal-finance/budget-calculator", "personal-finance/cost-of-living-calculator", "personal-finance/emergency-fund-calculator"],
    },
  },

  // 13. PGDM vs MBA Cost
  {
    slug: "pgdm-vs-mba-cost",
    category: "education",
    title: "PGDM vs MBA Cost Comparison",
    shortTitle: "PGDM vs MBA",
    description: "Compare total cost and ROI: PGDM vs. MBA programs",
    keywords: ["PGDM", "MBA", "cost comparison", "program comparison"],
    inputs: [
      { type: "number", key: "pgdmCost", label: "PGDM Total Cost (2 years)", prefix: "₹", min: 5000000, max: 50000000, step: 1000000, default: 20000000 },
      { type: "number", key: "mbaCost", label: "MBA Total Cost (2 years)", prefix: "₹", min: 5000000, max: 50000000, step: 1000000, default: 25000000 },
      { type: "number", key: "pgdmPackage", label: "PGDM Average Package", prefix: "₹", min: 500000, max: 50000000, step: 500000, default: 15000000 },
      { type: "number", key: "mbaPackage", label: "MBA Average Package", prefix: "₹", min: 500000, max: 50000000, step: 500000, default: 18000000 },
    ],
    calculate(v) {
      const pgdmCost = v.pgdmCost as number;
      const mbaCost = v.mbaCost as number;
      const pgdmPkg = v.pgdmPackage as number;
      const mbaPkg = v.mbaPackage as number;

      const pgdmROI = ((pgdmPkg - pgdmCost) / pgdmCost) * 100;
      const mbaROI = ((mbaPkg - mbaCost) / mbaCost) * 100;

      const costDiff = mbaCost - pgdmCost;
      const salaryDiff = mbaPkg - pgdmPkg;
      const better = pgdmROI > mbaROI ? "PGDM" : "MBA";

      return { pgdmROI: Math.round(pgdmROI), mbaROI: Math.round(mbaROI), costDiff, salaryDiff, better };
    },
    resultFields: [
      { key: "better", label: "Better Option (Based on ROI)", format: "text", isPrimary: true },
      { key: "pgdmROI", label: "PGDM ROI %", format: "percent" },
      { key: "mbaROI", label: "MBA ROI %", format: "percent" },
      { key: "costDiff", label: "Cost Difference (MBA - PGDM)", format: "currency" },
      { key: "salaryDiff", label: "Salary Difference (MBA - PGDM)", format: "currency" },
    ],
    content: {
      whatIs: "PGDM (Post Graduate Diploma in Management) vs MBA differ: PGDM is 2-year diploma (FDI-recognized but non-degree); MBA is 2-year degree from university. PGDM cost: ₹15-25L (ISB, XLRI, MDI); MBA: ₹8-80L depending on tier. PGDM salary: ₹15-20L average; MBA (IIM): ₹25-40L; MBA (tier-2): ₹12-18L. Both viable; PGDM from top institutes better ROI than MBA from average colleges.",
      faqs: [
        { question: "What's the difference between PGDM and MBA in India?", answer: "PGDM: 2-year diploma (by association/FDI). MBA: 2-year degree. Both AICTE-approved. PGDM from top institutes (ISB, XLRI) > MBA from average colleges. IIM MBA > any PGDM." },
        { question: "Which has better ROI: PGDM or MBA in India?", answer: "ISB PGDM: ₹15L cost, ₹20L package = excellent ROI. IIM MBA: ₹8L cost, ₹30L package = best ROI. Tier-2 MBA: ₹15L cost, ₹12L package = poor ROI." },
        { question: "Is PGDM recognized globally like MBA?", answer: "PGDM from top institutes (ISB, XLRI) recognized globally. Many PGDM programs FDI-approved. MBA degree more portable/recognized universally." },
        { question: "What's the average salary after PGDM in India?", answer: "ISB/XLRI PGDM: ₹18-25L. Tier-2 PGDM: ₹10-15L. Top tier: ₹25-35L. Placement rate: 90-98% for reputed PGDMs." },
        { question: "Can PGDM graduates pursue further studies (PhD, MS) in India?", answer: "MBA degree more recognized for PhD. PGDM graduates can pursue MS abroad. For Indian doctoral programs: check institution acceptance (some require bachelor's)." },
      ],
      relatedSlugs: ["education/education-loan-emi", "education/education-roi-calculator", "education/education-cost-planner"],
    },
  },

  // 14. Study Loan Comparison
  {
    slug: "study-loan-comparison",
    category: "education",
    title: "Education Loan Offer Comparison",
    shortTitle: "Loan Offer Comparison",
    description: "Compare education loan EMI across different lenders and tenure",
    keywords: ["education loan", "loan comparison", "bank comparison", "interest rate comparison"],
    inputs: [
      { type: "number", key: "loanAmount", label: "Loan Amount Needed", prefix: "₹", min: 500000, max: 5000000, step: 500000, default: 1500000 },
      { type: "number", key: "bank1Rate", label: "Bank 1 Interest Rate", suffix: "% p.a.", min: 7, max: 12, step: 0.25, default: 9.5 },
      { type: "number", key: "bank2Rate", label: "Bank 2 Interest Rate", suffix: "% p.a.", min: 7, max: 12, step: 0.25, default: 9 },
      { type: "number", key: "bank3Rate", label: "Bank 3 Interest Rate", suffix: "% p.a.", min: 7, max: 12, step: 0.25, default: 8.5 },
      { type: "number", key: "tenure", label: "Repayment Tenure", suffix: "years", min: 5, max: 15, step: 1, default: 7 },
    ],
    calculate(v) {
      const amount = v.loanAmount as number;
      const rate1 = (v.bank1Rate as number) / 100 / 12;
      const rate2 = (v.bank2Rate as number) / 100 / 12;
      const rate3 = (v.bank3Rate as number) / 100 / 12;
      const tenure = v.tenure as number;
      const months = tenure * 12;

      const calculateEMI = (r: number) => {
        if (r === 0) return amount / months;
        return (amount * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
      };

      const emi1 = Math.round(calculateEMI(rate1));
      const emi2 = Math.round(calculateEMI(rate2));
      const emi3 = Math.round(calculateEMI(rate3));

      const totalInterest1 = Math.round(emi1 * months - amount);
      const totalInterest2 = Math.round(emi2 * months - amount);
      const totalInterest3 = Math.round(emi3 * months - amount);

      const bestBank = totalInterest1 < totalInterest2 && totalInterest1 < totalInterest3 ? "Bank 1" : totalInterest2 < totalInterest3 ? "Bank 2" : "Bank 3";

      return {
        emi1, emi2, emi3,
        totalInterest1, totalInterest2, totalInterest3,
        interestSaved: Math.max(totalInterest1, totalInterest2, totalInterest3) - Math.min(totalInterest1, totalInterest2, totalInterest3),
        bestBank
      };
    },
    resultFields: [
      { key: "bestBank", label: "Best Bank (Lowest Interest)", format: "text", isPrimary: true },
      { key: "emi1", label: "Bank 1 EMI", format: "currency" },
      { key: "emi2", label: "Bank 2 EMI", format: "currency" },
      { key: "emi3", label: "Bank 3 EMI", format: "currency" },
      { key: "interestSaved", label: "Max Interest Savings", format: "currency" },
    ],
    content: {
      whatIs: "Education loan interest rates vary 8.5-10% across banks. Small rate differences compound significantly: 0.5% difference on ₹15L loan over 7 years = ₹50k+ interest savings. Compare: interest rate, processing fee (0.5-2%), moratorium period (6-12 months), prepayment penalty, and collateral requirements. Public banks (SBI, BOB): lower rates (8-9%), slower approval. Private banks (ICICI, HDFC): higher rates (9-10%), faster approval. Digital lending: faster but higher rates.",
      faqs: [
        { question: "Which bank offers best education loan rates in India?", answer: "SBI, BOB, IDBI: 8-8.5% (best rates, slow approval). ICICI, HDFC: 9-9.5%. NBFCs: 10-12% (easy approval, higher cost). Rates vary by profile." },
        { question: "What processing fee is charged on education loans?", answer: "Typically 0.5-2% of loan amount. SBI: 0.5-1%. Private: 1-2%. Some banks waive fee for online applications. Always ask." },
        { question: "Should I take education loan from bank or NBFC in India?", answer: "Bank: lower rates (8-9%), reliable. NBFC: higher rates (10-12%), easier approval, more flexible. Bank better if eligible; NBFC if urgent/weak profile." },
        { question: "Can I switch education loan bank later in India?", answer: "Yes, balance transfer possible. Get balance transfer quote from new bank (may incur 1-2% fee). Check if worth it (difference should be >1-2%)." },
        { question: "What fees and charges should I check on education loan in India?", answer: "Processing fee (0.5-2%), prepayment penalty (0-2%), late payment charges (1-2%), documentation/admin fees. Total fees can add ₹25-50k." },
      ],
      relatedSlugs: ["education/education-loan-emi", "education/education-cost-planner", "personal-finance/emi-vs-savings"],
    },
  },

  // 15. Education Inflation Calculator
  {
    slug: "education-inflation-calculator",
    category: "education",
    title: "Education Inflation Impact Calculator",
    shortTitle: "Education Inflation",
    description: "Project education costs 5-20 years ahead with inflation",
    keywords: ["education inflation", "inflation impact", "cost inflation", "future planning"],
    inputs: [
      { type: "number", key: "currentEducationCost", label: "Current Annual Education Cost", prefix: "₹", min: 100000, max: 5000000, step: 100000, default: 500000 },
      { type: "number", key: "yearsAhead", label: "Years in Future", suffix: "years", min: 1, max: 30, step: 1, default: 10 },
      { type: "number", key: "educationInflation", label: "Education Inflation Rate", suffix: "%", min: 5, max: 20, step: 0.5, default: 8 },
    ],
    calculate(v) {
      const cost = v.currentEducationCost as number;
      const years = v.yearsAhead as number;
      const inflation = (v.educationInflation as number) / 100;

      const futureCost = Math.round(cost * Math.pow(1 + inflation, years));
      const increase = futureCost - cost;
      const increasePercent = Math.round((increase / cost) * 100);

      return { currentCost: cost, futureCost, increase, increasePercent, years };
    },
    resultFields: [
      { key: "futureCost", label: "Future Annual Cost", format: "currency", isPrimary: true },
      { key: "currentCost", label: "Current Annual Cost", format: "currency" },
      { key: "increase", label: "Total Increase", format: "currency" },
      { key: "increasePercent", label: "Increase %", format: "percent" },
    ],
    content: {
      whatIs: "Education inflation (8-10% annually) outpaces general inflation (5-6%) in India. A ₹5L/year school fee becomes ₹10.8L in 10 years at 8% inflation. Impact: education costs double every 9-10 years. Planning must account for this: SIP investments must grow faster than inflation. Starting early (age 5) vs late (age 12) reduces monthly SIP burden by 50-100%. Failing to plan for inflation leads to shortfall and education debt.",
      faqs: [
        { question: "Why is education inflation higher than general inflation in India?", answer: "Infrastructure costs, faculty salaries, technology investment grow faster. Premium institutions (10-15% annually) worse than government (5-7%). Higher education more inflation-prone." },
        { question: "How much will a ₹10L annual fee become in 15 years at 8% inflation?", answer: "₹10L × (1.08^15) = ₹31.7L. Three-fold increase in 15 years. Critical for long-term education planning." },
        { question: "Should education SIP return exceed inflation?", answer: "Yes, absolutely. SIP should earn 12%+ returns (inflation: 8%, buffer: 4%+). Savings account (3-4% return) loses purchasing power." },
        { question: "How do I protect against education inflation in India?", answer: "Invest in equity funds (12%+ returns), start early (power of compounding), increase SIP annually (1-5% increase), use education insurance for backup." },
        { question: "What if inflation is lower than expected in education costs?", answer: "Unlikely but possible (policy intervention). Having extra corpus is beneficial: can fund additional education (overseas, higher qualification) or other goals." },
      ],
      relatedSlugs: ["education/education-cost-planner", "education/child-education-fund", "personal-finance/inflation-calculator"],
    },
  },
];
